import type { VidRange } from "~/units/scripture";
import { dumpRange, renderRangeReference } from "~/units/scripture";
import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRef } from "vue";
import { ResultAsync } from "neverthrow";
import type { ScriptureClientInterface } from "@rsc/scripture-client";
import {
  makeResultAsyncSerializable,
  type SerializableResult,
} from "~/errors/util";
import type { LogFunction } from "~/logging/log";

/**
 * Fetches human-readable references for a list of scripture ranges.
 *
 * @param scriptureClient The scripture client for loading verse data.
 * @param logServerSideError A callback to log errors when running on the server.
 * @param ranges The scripture ranges to render.
 * @param localTid The ID of a translation in the user's local language.
 * @param showTranslation Whether to show the translation of the scripture ranges.
 * @param plainText Whether to render the scripture ranges as plain text.
 */
export function useHumanReadableRanges(
  scriptureClient: ScriptureClientInterface,
  logServerSideError: LogFunction,
  ranges: MaybeRef<VidRange[]>,
  localTid: MaybeRef<string>,
  showTranslation: MaybeRef<boolean>,
  plainText: MaybeRef<boolean>,
) {
  return useQuery<SerializableResult<string[]>>({
    queryKey: [
      "humanReadableRanges",
      computed(() => unref(ranges).map((r) => dumpRange(r))),
      localTid,
      showTranslation,
      plainText,
    ],
    queryFn: () =>
      makeResultAsyncSerializable(() => {
        return ResultAsync.combine(
          unref(ranges).map((range: VidRange) =>
            renderRangeReference(
              range,
              scriptureClient,
              unref(localTid),
              unref(showTranslation),
              unref(plainText),
            ),
          ),
        );
      }, logServerSideError),
  });
}

/**
 * Fetches a human-readable reference for a scripture range.
 *
 * @param scriptureClient The scripture client for loading verse data.
 * @param logServerSideError A callback to log errors when running on the server.
 * @param range The scripture range to render.
 * @param localTid The ID of a translation in the user's local language.
 * @param showTranslation Whether to show the translation of the scripture range.
 * @param plainText Whether to render the scripture ranges as plain text.
 */
export function useHumanReadableRange(
  scriptureClient: ScriptureClientInterface,
  logServerSideError: LogFunction,
  range: MaybeRef<VidRange>,
  localTid: MaybeRef<string>,
  showTranslation: MaybeRef<boolean>,
  plainText: MaybeRef<boolean>,
) {
  return useQuery<SerializableResult<string>>({
    queryKey: [
      "humanReadableRange",
      computed(() => dumpRange(unref(range))),
      localTid,
      showTranslation,
      plainText,
    ],
    queryFn: () =>
      makeResultAsyncSerializable(
        () =>
          renderRangeReference(
            unref(range),
            scriptureClient,
            unref(localTid),
            unref(showTranslation),
            unref(plainText),
          ),
        logServerSideError,
      ),
  });
}
